import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import CustomInput from "shared/components/common/customInput";
import CustomTextEditor from "shared/components/common/customTextEditor";
import { toastMessage } from "shared/components/common/toast";
import { Blog, BlogFormData } from "shared/types/blog";
import { blogValidationSchema } from "shared/utils/validation";
import styles from "./style.module.scss";

interface BlogFormProps {
  initialData?: Blog;
  onSubmit: (data: BlogFormData) => void;
  onCancel: () => void;
  loading?: boolean;
}

const BlogForm: React.FC<BlogFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  loading,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string>("");

  const initialValues: BlogFormData = {
    title: initialData?.title || "",
    content: initialData?.content || "",
    thumbnail: null,
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: blogValidationSchema,
    onSubmit: (value, action) => {
      handleCreateBlog(value);
    },
  });

  const {
    handleChange,
    handleSubmit,
    values,
    touched,
    errors,
    isSubmitting,
    setSubmitting,
    setFieldValue,
    resetForm,
  } = formik;

  useEffect(() => {
    if (initialData?.image?.fullpath) {
      setPreviewUrl(initialData.image.fullpath);
    }
    if (initialData?.content) {
      setFieldValue("content", initialData.content);
    }
    if (initialData?.title) {
      setFieldValue("title", initialData.title);
    }
  }, [initialData]);

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const file = e.target.files?.[0] || null;
    setFieldValue("thumbnail", file);

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateBlog = (values: BlogFormData) => {
    if (!values.thumbnail && !initialData?.image?.fullpath) {
      toastMessage("error", "Thumbnail is required");
      return;
    }
    onSubmit(values);
    setSubmitting(false);
  };

  const defaultModules = {
    toolbar: [
      [{ header: "2" }, { font: [] }],
      [{ size: [] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      ["link", "image"],
      ["clean"],
    ],
  };

  return (
    <div className={styles.form}>
      <CustomInput
        label="Title"
        name="title"
        value={values.title}
        error={touched.title && errors.title}
        placeholder="Enter blog title"
        onChange={handleChange("title")}
      />

      <CustomTextEditor
        label="Content"
        value={values.content}
        error={touched.content && errors.content}
        placeholder="Write your blog content here..."
        onChange={(value) => setFieldValue("content", value)}
        modules={defaultModules}
      />

      <div className={styles.fileInputContainer}>
        <label className={styles.fileLabel}>
          Thumbnail Image
          <span className={styles.required}>*</span>
        </label>
        <input
          type="file"
          id="thumbnail"
          name="thumbnail"
          accept="image/*"
          onChange={(e) => handleFileChange(e, setFieldValue)}
          className={styles.fileInput}
        />
        {errors.thumbnail && touched.thumbnail && (
          <div className={styles.error}>{errors.thumbnail}</div>
        )}
        {previewUrl && (
          <div className={styles.previewContainer}>
            <img
              src={previewUrl}
              alt="Thumbnail preview"
              className={styles.previewImage}
            />
          </div>
        )}
      </div>

      <div className={styles.buttonContainer}>
        <button
          type="button"
          onClick={onCancel}
          className={styles.cancelButton}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className={styles.submitButton}
          disabled={loading}
          onClick={() => {
            handleSubmit();
          }}
        >
          {loading ? (
            <div className={styles.loaderContainer}>
              <div className={styles.loader} />
              <span>Processing...</span>
            </div>
          ) : initialData ? (
            "Update Blog"
          ) : (
            "Create Blog"
          )}
        </button>
      </div>
    </div>
  );
};

export default BlogForm;
